import React, { useEffect, useMemo, useRef, useState } from "react"
import PropTypes from "prop-types"
import UnifiedDatePicker from "./UnifiedDatePicker.compound"
import { DateTime } from "luxon"
import { getDateTime } from "../../../utils/dateTime.utils"
import {
  getClientPeriodByClientIdAndPeriodType,
  getClientSprintsByClientId,
} from "../../../services/clientReport.service"
import { UNIFIEDDATE_VALUES } from "../../../consts/CompoundRangeDatePicker"
import {
  CLIENT_CONSUMPTION_PERIOD_TYPE_ENUM,
  CLIENT_SPRINT_STATES,
} from "../../../consts/modules/clientReport.consts"
import { compareDates, getValidLuxonDate } from "../../../utils/date.utils"

const ClientReportPeriodSelector = ({
  refresh,
  dateRange,
  setDateRange,
  period,
  setPeriod,
  clientId,
  onlyAllowSprints = false,
  setSelectedPeriod,
  selectedPeriod,
  clientSprints,
  setClientSprints,
  setClientHasValidSprint,
  allowBlockPeriod = false,
  allowCyclePeriod = false,
  sprintBlocks = [],
  sprintCycles = [],
}) => {
  const previousPeriodRef = useRef(period)
  const [loading, setLoading] = useState(false)
  const [clientPeriods, setClientPeriods] = useState([])
  const normalizedSprintBlocks = useMemo(() => {
    return sprintBlocks
      .map((block) => {
        const id = Number(block?.id ?? block?.sprintBlockId)

        if (!Number.isFinite(id) || id <= 0) {
          return null
        }

        return {
          ...block,
          id,
          clientId: block?.clientId ?? clientId ?? selectedPeriod?.clientId,
          name: String(block?.blockNumberByClient ?? ""),
        }
      })
      .filter(Boolean)
  }, [clientId, selectedPeriod?.clientId, sprintBlocks])
  const normalizedSprintCycles = useMemo(() => {
    return sprintCycles
      .map((cycle, index) => {
        const id = Number(cycle?.id ?? cycle?.sprintCycleId)

        if (!Number.isFinite(id) || id <= 0) {
          return null
        }

        return {
          ...cycle,
          id,
          clientId: cycle?.clientId ?? clientId ?? selectedPeriod?.clientId,
          name:
            cycle?.name ||
            cycle?.sprintCycleName ||
            cycle?.cycleName ||
            `Ciclo ${index + 1}`,
        }
      })
      .filter(Boolean)
  }, [clientId, selectedPeriod?.clientId, sprintCycles])

  useEffect(() => {
    if (!allowBlockPeriod && period === UNIFIEDDATE_VALUES.BLOCK) {
      setPeriod(UNIFIEDDATE_VALUES.SPRINT)
      return
    }

    if (!allowCyclePeriod && period === UNIFIEDDATE_VALUES.CYCLE) {
      setPeriod(UNIFIEDDATE_VALUES.SPRINT)
    }
  }, [allowBlockPeriod, allowCyclePeriod, period, setPeriod])

  useEffect(() => {
    if (period !== UNIFIEDDATE_VALUES.BLOCK || !allowBlockPeriod) {
      return
    }

    if (!normalizedSprintBlocks.length) {
      return
    }

    const switchedToBlock =
      previousPeriodRef.current !== UNIFIEDDATE_VALUES.BLOCK
    if (switchedToBlock) {
      setSelectedPeriod(normalizedSprintBlocks[0])
      return
    }

    const selectedPeriodId = Number(selectedPeriod?.id)
    const selectedPeriodClientId = Number(selectedPeriod?.clientId)
    const hasSelectedBlock = normalizedSprintBlocks.some(
      (block) =>
        Number(block?.id) === selectedPeriodId &&
        Number(block?.clientId) === selectedPeriodClientId &&
        Number(block?.clientId) === Number(clientId)
    )

    if (!hasSelectedBlock) {
      setSelectedPeriod(normalizedSprintBlocks[0])
    }
  }, [
    allowBlockPeriod,
    normalizedSprintBlocks,
    clientId,
    period,
    selectedPeriod?.id,
    selectedPeriod?.clientId,
    setSelectedPeriod,
  ])

  useEffect(() => {
    if (period !== UNIFIEDDATE_VALUES.CYCLE || !allowCyclePeriod) {
      return
    }

    if (!normalizedSprintCycles.length) {
      return
    }

    const switchedToCycle =
      previousPeriodRef.current !== UNIFIEDDATE_VALUES.CYCLE
    if (switchedToCycle) {
      setSelectedPeriod(normalizedSprintCycles[0])
      return
    }

    const selectedPeriodId = Number(selectedPeriod?.id)
    const selectedPeriodClientId = Number(selectedPeriod?.clientId)
    const hasSelectedCycle = normalizedSprintCycles.some(
      (cycle) =>
        Number(cycle?.id) === selectedPeriodId &&
        Number(cycle?.clientId) === selectedPeriodClientId &&
        Number(cycle?.clientId) === Number(clientId)
    )

    if (!hasSelectedCycle) {
      setSelectedPeriod(normalizedSprintCycles[0])
    }
  }, [
    allowCyclePeriod,
    normalizedSprintCycles,
    clientId,
    period,
    selectedPeriod?.id,
    selectedPeriod?.clientId,
    setSelectedPeriod,
  ])

  useEffect(() => {
    if (period === UNIFIEDDATE_VALUES.SPRINT) {
      fetchClientSprints()
    } else if (
      period === UNIFIEDDATE_VALUES.BLOCK ||
      period === UNIFIEDDATE_VALUES.CYCLE
    ) {
      setClientPeriods([])
    } else {
      fetchClientPeriods()
    }
  }, [clientId, period, refresh])

  useEffect(() => {
    previousPeriodRef.current = period
  }, [period])

  const fetchClientSprints = async () => {
    setLoading(true)

    try {
      const response = await getClientSprintsByClientId(clientId)

      if (!response.success) {
        throw new Error(response.message)
      }

      const result = response.result

      if (result.length === 0) {
        const startDate = getValidLuxonDate(getDateTime())

        const placeholderSprint = {
          id: 0,
          startDate,
          endDate: null,
          sprintNumber: 1,
          state: "IN_PROGRESS",
          clientConsumptionPeriodType:
            CLIENT_CONSUMPTION_PERIOD_TYPE_ENUM.SPRINT,
          clientId,
        }

        setClientSprints([placeholderSprint])
        setSelectedPeriod(placeholderSprint)
        setDateRange([startDate, null])
        setClientHasValidSprint(false)
      } else {
        const formattedResult = result.map((sprint) => ({
          ...sprint,
          clientConsumptionPeriodType:
            CLIENT_CONSUMPTION_PERIOD_TYPE_ENUM.SPRINT,
        }))

        setDateRange([
          getValidLuxonDate(formattedResult[0].startDate),
          formattedResult[0].endDate ?
            getValidLuxonDate(formattedResult[0].endDate)
          : getValidLuxonDate(getDateTime()),
        ])

        const newSelectedPeriod =
          formattedResult.find((s) => s.id === selectedPeriod?.id) ||
          formattedResult.find(
            (s) => s.state === CLIENT_SPRINT_STATES.IN_PROGRESS
          ) ||
          formattedResult[0]

        setSelectedPeriod({
          ...newSelectedPeriod,
          clientId,
        })

        setClientSprints(formattedResult)
        setClientHasValidSprint(true)
      }

      setClientPeriods([])
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  const fetchClientPeriods = async () => {
    setLoading(true)
    try {
      let periodConsumption = ""
      if (period === UNIFIEDDATE_VALUES.SINGLEWEEK) {
        periodConsumption = CLIENT_CONSUMPTION_PERIOD_TYPE_ENUM.WEEKLY
      } else if (period === UNIFIEDDATE_VALUES.SINGLEMONTH) {
        periodConsumption = CLIENT_CONSUMPTION_PERIOD_TYPE_ENUM.MONTHLY
      } else {
        throw new Error("Invalid period type for fetching client periods")
      }
      const response = await getClientPeriodByClientIdAndPeriodType(
        clientId,
        periodConsumption
      )
      if (!response.success) {
        throw new Error(response.message)
      }
      const result = response.result
      if (result.length === 0) {
        const todayDate = getDateTime()
        const validPeriod = period?.split("-")[1]
        const startDate = todayDate.startOf(validPeriod)
        const endDate = todayDate.endOf(validPeriod)
        const placeholderPeriod = {
          id: 0,
          clientId,
          state: "IN_PROGRESS",
          periodNumberByClientAndType: 1,
          startDate: startDate.toISO(),
          endDate: endDate.toISO(),
          clientConsumptionPeriodType: periodConsumption,
        }
        setClientPeriods([placeholderPeriod])
        setSelectedPeriod(placeholderPeriod)
        setDateRange([startDate, endDate])
      } else {
        const latestPeriod = result[0]
        setDateRange([
          getValidLuxonDate(latestPeriod.startDate),
          latestPeriod.endDate ?
            getValidLuxonDate(latestPeriod.endDate)
          : getValidLuxonDate(getDateTime()),
        ])
        setSelectedPeriod(latestPeriod)
        setClientPeriods(result)
      }
      setClientSprints([])
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <UnifiedDatePicker
      className="sprint-picker"
      dateRange={dateRange}
      setDateRange={(newDateRange, _pickerPeriod) => {
        const [startDate, _endDate] = newDateRange
        const clientPeriod = clientPeriods.find((p) =>
          compareDates(
            getValidLuxonDate(p.startDate)?.startOf("day"),
            startDate?.startOf("day")
          )
        )
        if (clientPeriod) {
          setDateRange(newDateRange)
          setSelectedPeriod(clientPeriod)
        }
      }}
      period={period}
      setPeriod={setPeriod}
      disabledChevrons={
        loading ||
        (period === UNIFIEDDATE_VALUES.SPRINT ? clientSprints.length === 0
        : period === UNIFIEDDATE_VALUES.BLOCK ?
          normalizedSprintBlocks.length === 0
        : period === UNIFIEDDATE_VALUES.CYCLE ?
          normalizedSprintCycles.length === 0
        : clientPeriods.length === 0)
      }
      hasDayPeriod={false}
      hasSingleDay={false}
      hasSingleWeek={!onlyAllowSprints}
      hasSingleMonth={!onlyAllowSprints}
      hasSprintPeriod
      hasBlockPeriod={allowBlockPeriod}
      hasCyclePeriod={allowCyclePeriod}
      availableSprints={clientSprints}
      selectedSprint={selectedPeriod}
      setSelectedSprint={(sprint) => {
        setSelectedPeriod(sprint)
        setDateRange([
          getValidLuxonDate(sprint.startDate),
          sprint.endDate ?
            getValidLuxonDate(sprint.endDate)
          : getValidLuxonDate(getDateTime()),
        ])
      }}
      availableBlocks={normalizedSprintBlocks}
      availableCycles={normalizedSprintCycles}
      availablePeriods={clientPeriods}
      selectedPeriod={selectedPeriod}
      setSelectedPeriod={(period) => {
        setSelectedPeriod(period)
        setDateRange([
          getValidLuxonDate(period.startDate),
          period.endDate ?
            getValidLuxonDate(period.endDate)
          : getValidLuxonDate(getDateTime()),
        ])
      }}
      selectedCycle={selectedPeriod}
      setSelectedCycle={(cycle) => {
        setSelectedPeriod(cycle)
        setDateRange([
          getValidLuxonDate(cycle.startDate),
          cycle.endDate ?
            getValidLuxonDate(cycle.endDate)
          : getValidLuxonDate(getDateTime()),
        ])
      }}
      datePickerProps={{
        disabledDate: (current) => {
          if (
            period === UNIFIEDDATE_VALUES.SPRINT ||
            period === UNIFIEDDATE_VALUES.BLOCK ||
            period === UNIFIEDDATE_VALUES.CYCLE
          ) {
            return false
          }
          const validLuxonPeriod = period?.split("-")[1]
          return !clientPeriods.some((s) => {
            const start = getValidLuxonDate(s.startDate).startOf(
              validLuxonPeriod
            )
            const end =
              s.endDate ?
                getValidLuxonDate(s.endDate)
              : getValidLuxonDate(getDateTime()).endOf(validLuxonPeriod)
            return start <= current && current <= end
          })
        },
      }}
    />
  )
}

export default ClientReportPeriodSelector

ClientReportPeriodSelector.propTypes = {
  refresh: PropTypes.bool.isRequired,
  dateRange: PropTypes.arrayOf(PropTypes.instanceOf(DateTime)).isRequired,
  setDateRange: PropTypes.func.isRequired,
  period: PropTypes.string.isRequired,
  setPeriod: PropTypes.func.isRequired,
  clientId: PropTypes.number.isRequired,
  onlyAllowSprints: PropTypes.bool,
  setSelectedPeriod: PropTypes.func.isRequired,
  selectedPeriod: PropTypes.object,
  clientSprints: PropTypes.arrayOf(PropTypes.object),
  setClientSprints: PropTypes.func,
  setClientHasValidSprint: PropTypes.func,
  allowBlockPeriod: PropTypes.bool,
  allowCyclePeriod: PropTypes.bool,
  sprintBlocks: PropTypes.arrayOf(PropTypes.object),
  sprintCycles: PropTypes.arrayOf(PropTypes.object),
}
